import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError, api, onUnauthorized, setCsrfToken, setTokenProvider } from './api';
import { clearQueryCache } from './query';
import { proxyBase } from './base';
import { advanceIdentity, identityEpoch, StaleIdentityError } from './identity';
import { AuthTransitionGate } from './authTransition';
import { affiliateBootstrapCredentials, isEmbeddedAdmin, scrubLaunchToken, shopifyIdToken, type EmbeddedSession } from './embedded';
import type { MeResponse, Role } from './types';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { isSitePath } from '../pages/site/navigation';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous' | 'error';

interface AuthContextValue {
  status: AuthStatus;
  me: MeResponse | null;
  error: ApiError | null;
  /** True when the server signals demo mode for this session. */
  demoMode: boolean;
  /** Set when a previously valid session was rejected (expired/revoked). */
  sessionExpired: boolean;
  /** A cookie-changing auth request is in flight. */
  transitionPending: boolean;
  /** Set inside Shopify admin (App Home): the verified shop and whether a Perzo owner login exists. */
  embedded: EmbeddedSession | null;
  refresh: () => Promise<MeResponse | null>;
  login: (email: string, password: string) => Promise<MeResponse | null>;
  devLogin: (role: Role) => Promise<MeResponse | null>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function isDemoSession(me: MeResponse | null): boolean {
  if (!me) return false;
  if (me.demoMode === true) return true;
  if (me.demo === true) return true;
  if (me.demo && typeof me.demo === 'object' && me.demo.enabled) return true;
  return false;
}

/**
 * This browser signed in to Perzo before (set when /api/auth/me answers, cleared when it says
 * signed out). Only used to spare anonymous visitors of the public site a /api/auth/me probe that
 * answers 401 (a console error and a request on every page): it never grants anything, the server
 * decides on every request. Unreadable storage counts as "maybe": the probe runs as before.
 * Rollout edge: a session made before this hint existed shows "Log in" on site pages until the browser
 * opens any app page once (that probe sets the hint); app pages themselves always probe.
 */
const SIGNED_IN_HINT = 'perzo.signedIn';
function signedInHint(): boolean {
  try {
    return window.localStorage.getItem(SIGNED_IN_HINT) === '1';
  } catch {
    return true;
  }
}
function setSignedInHint(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(SIGNED_IN_HINT, '1');
    else window.localStorage.removeItem(SIGNED_IN_HINT);
  } catch {
    // Storage blocked: the probe simply keeps running on site pages.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Inside the app's router the provider knows the page (site page or not); rendered alone it probes as always.
  return useInRouterContext() ? <RoutedAuthProvider>{children}</RoutedAuthProvider> : <AuthProviderAt pathname={null}>{children}</AuthProviderAt>;
}

function RoutedAuthProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <AuthProviderAt pathname={pathname}>{children}</AuthProviderAt>;
}

function AuthProviderAt({ children, pathname }: { children: ReactNode; pathname: string | null }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  /** A public site page opened by a browser that never signed in: the session probe waits until it leaves the site. */
  const [probeSkipped, setProbeSkipped] = useState(false);
  const leftSite = probeSkipped && pathname !== null && !isSitePath(pathname);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [transitionPending, setTransitionPending] = useState(false);
  const [embedded, setEmbedded] = useState<EmbeddedSession | null>(null);
  const embeddedRef = useRef<EmbeddedSession | null>(null);
  const transitionGate = useRef(new AuthTransitionGate());
  const meRef = useRef<MeResponse | null>(null);
  const refreshSequence = useRef(0);
  const refreshUncertainRef = useRef(false);
  meRef.current = me;

  const refresh = useCallback(async () => {
    const epoch = identityEpoch();
    const requestId = ++refreshSequence.current;
    try {
      // Inside Shopify admin the first call connects the store (token exchange if needed) and maps the
      // staff member; afterwards every request authenticates with its own fresh ID token.
      if (isEmbeddedAdmin() && !embeddedRef.current) {
        const credentials = affiliateBootstrapCredentials();
        const session = await api<EmbeddedSession>('/shopify/embedded/bootstrap', { method: 'POST', silentUnauthorized: true, ...(credentials ? { credentials } : {}) });
        if (epoch !== identityEpoch() || requestId !== refreshSequence.current) throw new StaleIdentityError();
        embeddedRef.current = session;
        setEmbedded(session);
      }
      const result = await api<MeResponse>('/api/auth/me', { silentUnauthorized: true });
      if (epoch !== identityEpoch() || requestId !== refreshSequence.current) throw new StaleIdentityError();
      setCsrfToken(result.csrfToken ?? null);
      setSignedInHint(true);
      setMe(result);
      setError(null);
      setStatus('authenticated');
      refreshUncertainRef.current = false;
      return result;
    } catch (err) {
      if (epoch !== identityEpoch() || requestId !== refreshSequence.current || err instanceof StaleIdentityError || (err instanceof DOMException && err.name === 'AbortError')) return null;
      setCsrfToken(null);
      setMe(null);
      // Shopify admin has no Perzo sign-in page: a 401 there is an error to show, not "signed out".
      if (err instanceof ApiError && err.status === 401 && !isEmbeddedAdmin()) {
        setSignedInHint(false);
        refreshUncertainRef.current = false;
        setError(null);
        setStatus('anonymous');
        return null;
      }
      setError(err instanceof ApiError ? err : new ApiError(0, 'client_error', String(err)));
      setStatus('error');
      refreshUncertainRef.current = true;
      return null;
    }
  }, []);

  useEffect(() => {
    if (isEmbeddedAdmin()) {
      setTokenProvider(shopifyIdToken);
      scrubLaunchToken();
    }
    // On the shop's domain (App Proxy) there is no merchant session to probe.
    if (proxyBase()) {
      setStatus('anonymous');
      return;
    }
    // A site page (home, pricing, legal…) in a browser that never signed in: nobody to probe for.
    if (pathname !== null && isSitePath(pathname) && !isEmbeddedAdmin() && !signedInHint()) {
      setProbeSkipped(true);
      setStatus('anonymous');
      return;
    }
    void refresh();
    // Once, on mount (as before): later pages are handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  // Leaving the site for a page that needs the session (log in, dashboard): probe now, as a fresh load would.
  useEffect(() => {
    if (!leftSite) return;
    setProbeSkipped(false);
    setStatus('loading');
    void refresh();
  }, [leftSite, refresh]);

  useEffect(
    () =>
      onUnauthorized(() => {
        if (isEmbeddedAdmin()) {
          // Uninstall, a blocked store or disabled staff access. Shopify owns the session; reload from there.
          advanceIdentity();
          setMe(null);
          clearQueryCache();
          setError(new ApiError(401, 'shopify_session_ended', 'Your Perzo access in Shopify admin ended. Reload Perzo from Shopify admin.'));
          setStatus('error');
          return;
        }
        if (transitionGate.current.deferUnauthorized()) return;
        advanceIdentity();
        setSignedInHint(false);
        setCsrfToken(null);
        if (meRef.current) setSessionExpired(true);
        setMe(null);
        clearQueryCache();
        setStatus('anonymous');
      }),
    [],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      transitionGate.current.begin();
      setTransitionPending(true);
      try {
        if (refreshUncertainRef.current) {
          await refresh();
          if (refreshUncertainRef.current) throw new ApiError(0, 'session_state_unknown', 'Could not confirm the current session. Try again when the server is reachable.');
        }
        advanceIdentity();
        setCsrfToken(null);
        clearQueryCache();
        await api('/api/auth/login', { method: 'POST', body: { email, password }, silentUnauthorized: true });
        setSessionExpired(false);
        const result = await refresh();
        if (refreshUncertainRef.current) throw new ApiError(0, 'session_state_unknown', 'Sign-in may have completed, but the session could not be confirmed.');
        return result;
      } catch (err) {
        await refresh();
        throw err;
      } finally {
        const reconcile = transitionGate.current.finish();
        setTransitionPending(false);
        if (reconcile) void refresh();
      }
    },
    [refresh],
  );

  const devLogin = useCallback(
    async (role: Role) => {
      transitionGate.current.begin();
      setTransitionPending(true);
      try {
        if (refreshUncertainRef.current) {
          await refresh();
          if (refreshUncertainRef.current) throw new ApiError(0, 'session_state_unknown', 'Could not confirm the current session. Try again when the server is reachable.');
        }
        advanceIdentity();
        setCsrfToken(null);
        clearQueryCache();
        await api('/api/dev/login', { method: 'POST', body: { role }, silentUnauthorized: true });
        setSessionExpired(false);
        const result = await refresh();
        if (refreshUncertainRef.current) throw new ApiError(0, 'session_state_unknown', 'Demo sign-in may have completed, but the session could not be confirmed.');
        return result;
      } catch (err) {
        await refresh();
        throw err;
      } finally {
        const reconcile = transitionGate.current.finish();
        setTransitionPending(false);
        if (reconcile) void refresh();
      }
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    transitionGate.current.begin();
    setTransitionPending(true);
    let epoch = identityEpoch();
    let revoked = false;
    try {
      if (refreshUncertainRef.current) {
        await refresh();
        if (refreshUncertainRef.current) throw new ApiError(0, 'session_state_unknown', 'Could not confirm the current session. Try again when the server is reachable.');
      }
      advanceIdentity();
      epoch = identityEpoch();
      clearQueryCache();
      await api('/api/auth/logout', { method: 'POST', silentUnauthorized: true });
      revoked = true;
    } catch (err) {
      await refresh();
      throw err;
    } finally {
      if (revoked && identityEpoch() === epoch) {
        setSignedInHint(false);
        setCsrfToken(null);
        setMe(null);
        setSessionExpired(false);
        setStatus('anonymous');
      }
      const reconcile = transitionGate.current.finish();
      setTransitionPending(false);
      if (reconcile) void refresh();
    }
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    // On the way out of the site (before the probe's effect runs) nothing is known yet: never "signed out".
    () => ({ status: leftSite ? 'loading' : status, me, error, demoMode: isDemoSession(me), sessionExpired, transitionPending, embedded, refresh, login, devLogin, logout }),
    [leftSite, status, me, error, sessionExpired, transitionPending, embedded, refresh, login, devLogin, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** The auth context when there is one (optional surfaces rendered in isolation get null). */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export function homePathFor(me: MeResponse | null): string {
  if (!me) return '/login';
  return me.user.role === 'admin' ? '/admin' : '/app';
}
